import IconClose from '@/assets/smartcity/close-videomodal.svg';
import MaintenanceIcon from '@/assets/smartcity/maintenance-icon.svg';
import OfflineIcon from '@/assets/smartcity/offline-icon.svg';
import { useCctvStream } from '@/domains/traffic-monitoring/hooks/useCctvStream';
import { useColorStore } from '@/store/colorStore';

interface VideoModalMapsProps {
  address: string;
  onClick?: () => void;
  streamUrl?: string;
  name: string;
  latlong: string;
  typeVideo: 'online' | 'offline' | 'maintenance';
  isOpen?: boolean;
}

const VideoModalMaps = ({
  address,
  name,
  latlong,
  streamUrl,
  typeVideo,
  onClick,
  isOpen,
}: VideoModalMapsProps) => {
  const { NEWCOLOR } = useColorStore();
  const { videoRef, status } = useCctvStream({ streamUrl });

  if (!isOpen) {
    return null;
  }

  const statusConfig = {
    online: { text: 'Online', color: NEWCOLOR.greenPrimary, bg: '#1D293D80', icon: null },
    offline: { text: 'Offline', color: NEWCOLOR.danger, bg: '#FC66051A', icon: OfflineIcon },
    maintenance: {
      text: 'Maintenance',
      color: NEWCOLOR.warning,
      bg: '#FC66051A',
      icon: MaintenanceIcon,
    },
  };

  const statusInfo = statusConfig[typeVideo];
  const showVideo = typeVideo === 'online' && !!streamUrl && status !== 'error';
  const isBuffering = showVideo && (status === 'loading' || status === 'reconnecting');

  return (
    // Above the map's own CCTV cards, which SmartCityMaps portals in at z-index
    // 1000, and above the other dashboard layers (modals at 1000, datepicker at
    // 1100) so an expanded camera never sits behind its card.
    <div className="absolute left-1/2 top-1/2 z-[1200] flex w-[40%] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 rounded-[16px] bg-white p-5 shadow-lg">
      <div className="flex w-full items-center justify-between gap-3">
        <span className="min-w-0 flex-1 text-black truncate title1bold capitalize" title={address}>
          {name}
        </span>

        <button
          type="button"
          onClick={onClick}
          className="shrink-0 cursor-pointer"
          aria-label="zoom"
        >
          <img src={IconClose} alt="zoom" />
        </button>
      </div>

      <div className="relative aspect-video w-full overflow-hidden rounded-[10px] bg-black">
        <div
          className="absolute left-2 top-2 z-10 inline-flex items-center gap-2 rounded-full border px-3 py-1 backdrop-blur-sm"
          style={{ backgroundColor: statusInfo.bg, borderColor: statusInfo.color }}
        >
          <div className="h-2 w-2 rounded-full" style={{ backgroundColor: statusInfo.color }} />
          <span className="text-xs font-semibold uppercase" style={{ color: statusInfo.color }}>
            {statusInfo.text}
          </span>
        </div>

        {showVideo ? (
          <>
            {/* biome-ignore lint/a11y/useMediaCaption: live camera feed, no captions source */}
            <video ref={videoRef} muted playsInline className="h-full w-full object-cover" />
            {isBuffering && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <span className="text-xs text-white">
                  {status === 'reconnecting' ? 'Reconnecting…' : 'Connecting…'}
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-db-border">
            {statusInfo.icon ? (
              <img src={statusInfo.icon} alt={statusInfo.text} className="h-16 w-16" />
            ) : (
              <span className="text-sm text-db-grey">No Video</span>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-text-grey title2">{address}</span>
        <span className="text-text-grey title2">{latlong}</span>
      </div>
    </div>
  );
};

export default VideoModalMaps;
